import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  People,
  TrendingUp,
  AccessTime,
  Schedule,
  Notifications,
  Refresh,
  CheckCircle,
  Warning,
  Error,
  Info,
  TrendingDown,
  TrendingFlat,
  PersonAdd,
  Assessment,
  NetworkCheck,
  Coffee,
  Restaurant,
  Cancel,
  Add,
  EventBusy,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { dashboardAPI, apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { TimezoneService } from '../services/timezoneService';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import AttendanceTimingLegend from '../components/AttendanceTimingLegend';
import MissingAgentListItem from '../components/MissingAgentListItem';
import ShareMissingAgentsModal from '../components/dashboard/ShareMissingAgentsModal';
import { MissingAgentRow } from '../utils/attendanceLabels';
import {
  campaignChartHeight,
  departmentChartHeight,
  prepareCampaignChartData,
  truncateChartLabel,
} from '../utils/dashboardChartUtils';

interface DashboardStats {
  totalEmployees: number;
  activeEmployees: number;
  activeDepartments: number;
  activeCampaigns: number;
  todayAttendance: number;
  productivityScore: number;
  breakCompliance: number;
  systemHealth: number;
}

interface AlertItem {
  id: string;
  type: 'warning' | 'error' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: string;
  actionRequired?: boolean;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  const [shareMissingAgentsOpen, setShareMissingAgentsOpen] = useState(false);
  const canRecordAbsence = (user?.permissions || []).includes('absences.create');

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const canViewSupervisorDashboard = ['supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');
  const canViewLiveStatus = ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'].includes(user?.role || '');

  // Enhanced stats query
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats', refreshKey],
    queryFn: () => dashboardAPI.getStats(),
    refetchInterval: 30000,
    enabled: canViewSupervisorDashboard,
  });

  // Live employee status for real-time data
  const { data: liveStatusData } = useQuery({
    queryKey: ['liveEmployeeStatus', refreshKey],
    queryFn: async () => {
      const response = await apiClient.timeEntries.getLiveStatus();
      return response.data;
    },
    refetchInterval: 30000,
    enabled: canViewLiveStatus,
  });

  // Dashboard analytics data
  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ['dashboardAnalytics', refreshKey],
    queryFn: () => dashboardAPI.getAnalytics(),
    refetchInterval: 60000,
    enabled: canViewSupervisorDashboard,
  });

  // Recent activities — lightweight endpoint (today's last 10 entries)
  const { data: recentActivities, isLoading: activitiesLoading } = useQuery({
    queryKey: ['recentActivities', refreshKey],
    queryFn: async () => {
      const response = await dashboardAPI.getRecentActivity();
      return { data: response.data.data || [] };
    },
    refetchInterval: 15000,
    enabled: canViewSupervisorDashboard,
  });

  // Missing agents - only for supervisors and above
  const { data: missingAgents, isLoading: missingAgentsLoading, error: missingAgentsError } = useQuery({
    queryKey: ['missingAgents', refreshKey],
    queryFn: () => dashboardAPI.getMissingAgents(),
    refetchInterval: 30000,
    enabled: canViewSupervisorDashboard,
  });

  // Calculate real metrics from live data
  const totalEmployees = statsData?.data?.data?.totalEmployees || 0;
  const activeEmployees = statsData?.data?.data?.activeEmployees || 0;
  const activeDepartments = statsData?.data?.data?.activeDepartments || 0;
  const activeCampaigns = statsData?.data?.data?.activeCampaigns || 0;
  
  // Calculate today's attendance percentage
  const todayAttendance = liveStatusData?.data && liveStatusData.data.length > 0 ? 
    Math.round((liveStatusData.data.filter((emp: any) => emp.currentStatus === 'clocked_in').length / liveStatusData.data.length) * 100) : 0;

  // Get productivity score from analytics or calculate from live data
  const attendanceTrend = analyticsData?.data?.data?.attendanceTrend;
  const productivityScore = attendanceTrend?.length > 0 ? 
    Math.round((attendanceTrend.reduce((sum: number, day: any) => sum + day.productivity, 0) / attendanceTrend.length)) : 
    Math.round(todayAttendance * 0.9);

  // Calculate break compliance from live data
  const breakCompliance = liveStatusData?.data ? 
    Math.round((liveStatusData.data.filter((emp: any) => 
      emp.currentStatus === 'clocked_in' || emp.currentStatus === 'on_break' || emp.currentStatus === 'on_bathroom' || emp.currentStatus === 'on_lunch'
    ).length / liveStatusData.data.length) * 100) : 0;

  // Get system health from analytics
  const systemHealth = analyticsData?.data?.data?.systemHealth?.uptime || 98;

  const stats: DashboardStats = {
    totalEmployees,
    activeEmployees,
    activeDepartments,
    activeCampaigns,
    todayAttendance,
    productivityScore,
    breakCompliance,
    systemHealth,
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const missingAgentsList: MissingAgentRow[] = missingAgents?.data?.data || [];
  const missingAgentsWorkDate = new Date().toLocaleDateString('en-CA', {
    timeZone: user?.timezone || TimezoneService.getUserTimezone(),
  });

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'clock_in': return <CheckCircle color="success" />;
      case 'clock_out': return <Cancel color="error" />;
      case 'break_start': return <Coffee color="warning" />;
      case 'break_end': return <CheckCircle color="success" />;
      case 'lunch_start': return <Restaurant color="info" />;
      case 'lunch_end': return <CheckCircle color="success" />;
      case 'bathroom_start': return <Coffee color="warning" />;
      case 'bathroom_end': return <CheckCircle color="success" />;
      default: return <AccessTime />;
    }
  };

  const getActivityText = (activity: any) => {
    const typeMap: Record<string, string> = {
      'clock_in': 'clocked in',
      'clock_out': 'clocked out',
      'break_start': 'started break',
      'break_end': 'ended break',
      'lunch_start': 'started lunch',
      'lunch_end': 'ended lunch',
      'bathroom_start': 'started bathroom',
      'bathroom_end': 'ended bathroom',
    };
    const employeeName = activity.employeeName || 'Unknown User';
    const action = typeMap[activity.entryType] || 'performed action';
    return `${employeeName} ${action}`;
  };

  // Real data for charts from analytics API
  const attendanceTrendData = analyticsData?.data?.data?.attendanceTrend || [
    { day: 'Mon', attendance: 0, productivity: 0 },
    { day: 'Tue', attendance: 0, productivity: 0 },
    { day: 'Wed', attendance: 0, productivity: 0 },
    { day: 'Thu', attendance: 0, productivity: 0 },
    { day: 'Fri', attendance: 0, productivity: 0 },
    { day: 'Sat', attendance: 0, productivity: 0 },
    { day: 'Sun', attendance: 0, productivity: 0 },
  ];

  const departmentData = analyticsData?.data?.data?.departmentPerformance || [];

  const campaignChartData = useMemo(
    () => prepareCampaignChartData(analyticsData?.data?.data?.campaignDistribution || []),
    [analyticsData],
  );

  const campaignBarHeight = campaignChartHeight(campaignChartData.length);
  const departmentBarHeight = departmentChartHeight(Math.max(departmentData.length, 1));

  const alerts: AlertItem[] = [
    {
      id: '1',
      type: 'warning',
      title: 'Break Time Exceeded',
      message: '3 employees have exceeded their break time limit',
      timestamp: new Date().toISOString(),
      actionRequired: true,
    },
    {
      id: '2',
      type: 'info',
      title: 'System Update',
      message: 'Scheduled maintenance completed successfully',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: '3',
      type: 'success',
      title: 'High Productivity',
      message: 'Team productivity is 15% above target this week',
      timestamp: new Date(Date.now() - 7200000).toISOString(),
    },
  ];

  // Define quick action interface
  interface QuickAction {
    label: string;
    icon: React.ReactNode;
    path: string;
    color: string;
    action?: string;
    requiredPermission?: string;
  }

  // Get user permissions
  const permissions = user?.permissions || [];
  const hasPermission = (permission: string) => permissions.includes(permission);

  // Helper function to check if user has any of the specified permissions
  const hasAnyPermission = (permissionList: string[]) => {
    return permissionList.some(perm => hasPermission(perm));
  };

  // Define all possible quick actions with their permission requirements
  const allQuickActions: QuickAction[] = [
    { 
      label: 'View Live Status', 
      icon: <People />, 
      path: '/dashboard/live-status', 
      color: 'primary',
      requiredPermission: undefined // Available to all authenticated users
    },
    { 
      label: 'Manage Schedules', 
      icon: <Schedule />, 
      path: '/management/schedules', 
      color: 'secondary',
      requiredPermission: 'schedules.read'
    },
    { 
      label: 'Time Reports', 
      icon: <Assessment />, 
      path: '/reports', 
      color: 'info',
      requiredPermission: undefined // Check with hasAnyPermission
    },
    { 
      label: 'User Management', 
      icon: <PersonAdd />, 
      path: '/management/users', 
      color: 'success',
      requiredPermission: 'users.read'
    },
    { 
      label: 'Network Config', 
      icon: <NetworkCheck />, 
      path: '/system/network-config', 
      color: 'error',
      requiredPermission: undefined // Available to administrators (role-based)
    },
    { 
      label: 'Add Time Entry', 
      icon: <Add />, 
      path: '/management/time-records', 
      color: 'warning',
      action: 'add-entry',
      requiredPermission: 'time_entries.create'
    },
    { 
      label: 'Add Absence', 
      icon: <EventBusy />, 
      path: '/management/time-records', 
      color: 'error',
      action: 'add-absence',
      requiredPermission: 'absences.create'
    },
    { 
      label: 'Request Time Off', 
      icon: <EventBusy />, 
      path: '/time-absence-requests', 
      color: 'primary',
      requiredPermission: 'absences.create'
    },
  ];

  // Filter quick actions based on permissions
  const quickActions: QuickAction[] = allQuickActions.filter(action => {
    // If no permission required, show it (for View Live Status and Network Config)
    if (!action.requiredPermission) {
      // Network Config should only show for administrators
      if (action.label === 'Network Config') {
        return ['administrator', 'super_user'].includes(user?.role || '');
      }
      // Time Reports should show if user has time_entries.read or reports.read
      if (action.label === 'Time Reports') {
        return hasAnyPermission(['time_entries.read', 'reports.read']);
      }
      // View Live Status available to all
      return true;
    }
    
    // Check specific permission
    return hasPermission(action.requiredPermission);
  });

  const StatCard: React.FC<{ 
    title: string; 
    value: number | string; 
    icon: React.ReactNode; 
    color: string;
    trend?: 'up' | 'down' | 'flat';
    subtitle?: string;
  }> = ({ title, value, icon, color, trend, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend === 'up' && <TrendingUp color="success" fontSize="small" />}
                {trend === 'down' && <TrendingDown color="error" fontSize="small" />}
                {trend === 'flat' && <TrendingFlat color="info" fontSize="small" />}
                <Typography variant="caption" color="text.secondary" ml={0.5}>
                  vs yesterday
                </Typography>
              </Box>
            )}
          </Box>
          <Box color={color}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const AlertCard: React.FC<{ alert: AlertItem }> = ({ alert }) => {
    const getAlertIcon = () => {
      switch (alert.type) {
        case 'warning': return <Warning color="warning" />;
        case 'error': return <Error color="error" />;
        case 'info': return <Info color="info" />;
        case 'success': return <CheckCircle color="success" />;
        default: return <Notifications />;
      }
    };

    const handleViewClick = () => {
      // Navigate to appropriate page based on alert type
      switch (alert.id) {
        case '1': // Break Time Exceeded
          navigate('/dashboard/live-status');
          break;
        case '2': // System Update
          navigate('/system/status');
          break;
        case '3': // High Productivity
          navigate('/reports');
          break;
        default:
          navigate('/dashboard/live-status');
      }
    };

    return (
      <Alert 
        severity={alert.type} 
        icon={getAlertIcon()}
        action={alert.actionRequired ? (
          <Button size="small" color="inherit" onClick={handleViewClick}>
            View
          </Button>
        ) : undefined}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          {alert.title}
        </Typography>
        <Typography variant="body2">
          {alert.message}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(alert.timestamp).toLocaleTimeString()}
        </Typography>
      </Alert>
    );
  };

  if (canViewSupervisorDashboard && statsLoading && !statsData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.firstName}! Here's what's happening today.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: {currentTime.toLocaleTimeString('en-US', { 
              hour12: true, 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </Typography>
          <Tooltip title="Refresh Dashboard">
            <IconButton onClick={handleRefresh} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Executive Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Today's Attendance"
            value={`${stats.todayAttendance}%`}
            icon={<People fontSize="large" />}
            color="primary.main"
            trend="up"
            subtitle={`${stats.activeEmployees} of ${stats.totalEmployees} employees`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Productivity Score"
            value={`${stats.productivityScore}%`}
            icon={<TrendingUp fontSize="large" />}
            color="success.main"
            trend="up"
            subtitle="Above target"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Break Compliance"
            value={`${stats.breakCompliance}%`}
            icon={<Coffee fontSize="large" />}
            color="warning.main"
            trend="flat"
            subtitle="Within limits"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="System Health"
            value={`${stats.systemHealth}%`}
            icon={<NetworkCheck fontSize="large" />}
            color="info.main"
            trend="up"
            subtitle="All systems operational"
          />
        </Grid>
      </Grid>

      {/* Quick Actions and Recent Activity */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={1}>
                {quickActions.map((action, index) => (
                  <Grid item xs={6} key={index}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={action.icon}
                      onClick={() => {
                        if (action.action === 'add-entry') {
                          // Navigate to time records page and trigger add entry dialog
                          navigate('/management/time-records?action=add-entry');
                        } else if (action.action === 'add-absence') {
                          // Navigate to time records page and trigger add absence dialog
                          navigate('/management/time-records?action=add-absence');
                        } else {
                          // Regular navigation
                          navigate(action.path);
                        }
                      }}
                      sx={{ mb: 1 }}
                      color={action.color as any}
                    >
                      {action.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              {activitiesLoading ? (
                <LinearProgress />
              ) : (
                <List dense>
                  {recentActivities?.data?.slice(0, 5).map((activity: any, index: number) => (
                    <ListItem key={index} disablePadding>
                      <ListItemIcon>
                        {getActivityIcon(activity.entryType)}
                      </ListItemIcon>
                      <ListItemText
                        primary={getActivityText(activity)}
                        secondary={TimezoneService.formatTimeConsistent(activity.timestamp, user?.timezone || TimezoneService.getUserTimezone())}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Missing Agents - Only for supervisors and above */}
        {canViewSupervisorDashboard && (
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">Missing Agents</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ShareIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setShareMissingAgentsOpen(true)}
                    disabled={missingAgentsLoading || missingAgentsList.length === 0}
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.25,
                      fontSize: '0.7rem',
                      lineHeight: 1.2,
                      textTransform: 'none',
                      '& .MuiButton-startIcon': { mr: 0.35, ml: -0.15 },
                    }}
                  >
                    Share
                  </Button>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <AttendanceTimingLegend />
                </Box>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {missingAgentsError ? (
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
                      Error loading missing agents: {missingAgentsError.message}
                    </Typography>
                  ) : missingAgentsLoading ? (
                    <LinearProgress />
                  ) : missingAgentsList.length > 0 ? (
                    <List dense>
                      {missingAgentsList.map((agent: MissingAgentRow, index: number) => (
                        <MissingAgentListItem
                          key={agent.id || agent.scheduleId || String(index)}
                          agent={agent}
                          canRecordAbsence={canRecordAbsence}
                          onAbsenceRecorded={() => setRefreshKey((key) => key + 1)}
                        />
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No missing agents today
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Visual Analytics */}
      {canViewSupervisorDashboard && (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Attendance & Productivity Trends
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Daily attendance and productivity over the last 7 days
              </Typography>
              {analyticsLoading ? (
                <Box sx={{ py: 12 }}>
                  <LinearProgress />
                </Box>
              ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={attendanceTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis domain={[0, 100]} unit="%" />
                  <RechartsTooltip formatter={(value: number) => [`${value}%`, '']} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="attendance"
                    stroke="#1565c0"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Attendance %"
                  />
                  <Line
                    type="monotone"
                    dataKey="productivity"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name="Productivity %"
                  />
                </LineChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Campaign Activity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share of time-entry activity in the last 7 days (top campaigns shown)
              </Typography>
              {analyticsLoading ? (
                <Box sx={{ py: 12 }}>
                  <LinearProgress />
                </Box>
              ) : campaignChartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No campaign activity recorded this week
                </Typography>
              ) : (
              <ResponsiveContainer width="100%" height={campaignBarHeight}>
                <BarChart
                  layout="vertical"
                  data={campaignChartData}
                  margin={{ top: 4, right: 24, left: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" domain={[0, 'dataMax']} unit="%" tickFormatter={(value) => `${value}%`} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={140}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => truncateChartLabel(String(value), 20)}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => [`${value}%`, 'Activity share']}
                    labelFormatter={(label) => String(label)}
                  />
                  <Bar dataKey="value" name="Activity share" radius={[0, 4, 4, 0]} barSize={22}>
                    {campaignChartData.map((entry, index) => (
                      <Cell key={entry.name} fill={entry.color || `hsl(${(index * 47) % 360}, 65%, 48%)`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Department Performance */}
      {canViewSupervisorDashboard && (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Department Performance
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Active employees by department with productivity trend
              </Typography>
              {analyticsLoading ? (
                <Box sx={{ py: 12 }}>
                  <LinearProgress />
                </Box>
              ) : departmentData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No department data available
                </Typography>
              ) : (
              <ResponsiveContainer width="100%" height={departmentBarHeight}>
                <ComposedChart data={departmentData} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-28}
                    textAnchor="end"
                    height={72}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => truncateChartLabel(String(value), 14)}
                  />
                  <YAxis yAxisId="employees" allowDecimals={false} />
                  <YAxis yAxisId="productivity" orientation="right" domain={[0, 100]} unit="%" />
                  <RechartsTooltip />
                  <Legend />
                  <Bar yAxisId="employees" dataKey="employees" fill="#5c6bc0" name="Employees" radius={[4, 4, 0, 0]} />
                  <Line
                    yAxisId="productivity"
                    type="monotone"
                    dataKey="productivity"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name="Productivity %"
                  />
                </ComposedChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Overview
              </Typography>
              {analyticsLoading ? (
                <Box sx={{ py: 12 }}>
                  <LinearProgress />
                </Box>
              ) : (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Database Status</Typography>
                  <Chip 
                    label={analyticsData?.data?.data?.systemHealth?.databaseStatus || 'Unknown'} 
                    color={analyticsData?.data?.data?.systemHealth?.databaseStatus === 'healthy' ? 'success' : 'error'} 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">API Response Time</Typography>
                  <Chip 
                    label={`${analyticsData?.data?.data?.systemHealth?.apiResponseTime || 0}ms`} 
                    color={analyticsData?.data?.data?.systemHealth?.apiResponseTime < 100 ? 'success' : 'warning'} 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Active Sessions</Typography>
                  <Chip 
                    label={`${analyticsData?.data?.data?.systemHealth?.activeSessions || stats.activeEmployees}`} 
                    color="info" 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Uptime</Typography>
                  <Chip 
                    label={`${analyticsData?.data?.data?.systemHealth?.uptime || 98}%`} 
                    color={analyticsData?.data?.data?.systemHealth?.uptime > 99 ? 'success' : 'warning'} 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Email Service</Typography>
                  <Chip 
                    label={analyticsData?.data?.data?.systemHealth?.emailConnectivity === 'healthy' ? 'ON' : 'OFF'} 
                    color={analyticsData?.data?.data?.systemHealth?.emailConnectivity === 'healthy' ? 'success' : 'error'} 
                    size="small" 
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2">Alert Service</Typography>
                  <Chip 
                    label={analyticsData?.data?.data?.systemHealth?.alertSystemHealth === 'healthy' ? 'ON' : 'OFF'} 
                    color={analyticsData?.data?.data?.systemHealth?.alertSystemHealth === 'healthy' ? 'success' : 'error'} 
                    size="small" 
                  />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last System Check: {currentTime.toLocaleTimeString('en-US', { 
                    hour12: true, 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Next Scheduled Maintenance: Tomorrow 2:00 AM
                </Typography>
              </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      <ShareMissingAgentsModal
        open={shareMissingAgentsOpen}
        onClose={() => setShareMissingAgentsOpen(false)}
        agents={missingAgentsList}
        workDate={missingAgentsWorkDate}
      />
    </Container>
  );
};

export default DashboardPage;
